if (!customElements.get('custom-select')) {
  class CustomSelect extends HTMLElement {
    static observedAttributes = ['open'];

    static #DESKTOP_GRID_MQ = '(min-width: 1024px)';

    #updatingOpen = false;
    #desktopGridMq = window.matchMedia(CustomSelect.#DESKTOP_GRID_MQ);
    #handleDesktopGridChange = () => this.#syncDesktopGridMode();

    constructor() {
      super();

      this.elToggle = this.querySelector('[data-toggle]');
      this.elInputs = this.querySelectorAll('input');
      this.elOptions = this.querySelector('[data-options]');
      this.elSelected = this.querySelector('[data-selected]');
      this.elAnnounce = this.querySelector('[data-announce]');

      this.open = false;
      this.suppressFocusReturn = false;
      this.value = [...this.elInputs].find(el => el.checked)?.value;

      this.handleToggleClick = this.toggle.bind(this);
      this.handleInputChange = this.select.bind(this);
      this.handleLabelClickBound = this.handleLabelClick.bind(this);
      this.handleKeydownBound = this.handleKeydown.bind(this);
      this.handleDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
    }

    connectedCallback() {
      this.elToggle.addEventListener('click', this.handleToggleClick);

      for (const elInput of this.elInputs) {
        elInput.addEventListener('change', this.handleInputChange);

        const elLabel = elInput.labels && elInput.labels[0];
        if (elLabel) {
          elLabel.addEventListener('click', this.handleLabelClickBound);
        }
      }

      this.addEventListener('keydown', this.handleKeydownBound);
      this.#desktopGridMq.addEventListener('change', this.#handleDesktopGridChange);
      this.#syncDesktopGridMode();
    }

    disconnectedCallback() {
      this.#syncOutsideCloseListener();
      this.#desktopGridMq.removeEventListener('change', this.#handleDesktopGridChange);
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name !== 'open' || this.#updatingOpen) return;

      const nextOpen = newValue === 'true';

      if (nextOpen && this.#isDesktopGrid()) {
        this.#updatingOpen = true;
        this.setAttribute('open', 'false');
        this.#updatingOpen = false;
        return;
      }

      if (nextOpen === this.open) return;

      this.open = nextOpen;
      this.classList.toggle('open', this.open);
      this.elToggle.setAttribute('aria-expanded', String(this.open));

      if (this.open) {
        this.#handleOpen();
      } else {
        this.#handleClose();
      }
    }

    toggle() {
      if (this.#isDesktopGrid()) return;
      this.setAttribute('open', String(!this.open));
    }

    select(ev) {
      const elInput = ev.currentTarget;
      const label = elInput.labels[0]?.textContent?.trim();
      this.elSelected.textContent = label;
      this.value = elInput.value;
    }

    commit(elInput) {
      if (this.elAnnounce) {
        const label = elInput.labels[0].textContent.trim();
        this.elAnnounce.textContent = `${label} selected.`;
      }

      if (this.#isDesktopGrid()) return;
      this.setAttribute('open', 'false');
    }

    handleLabelClick(ev) {
      // Some browsers (notably Firefox) fire `click` on a radio input as a
      // side-effect of arrow-key navigation, so we listen on the label
      // instead — label clicks only happen for real mouse/touch interactions.
      // We take over the label's default activation so we can guarantee
      // the order of events (set checked → fire change → close).
      ev.preventDefault();

      const elInput = ev.currentTarget.control;
      if (!elInput) return;

      if (!elInput.checked) {
        this.elInputs.forEach(el => el.checked = false);
        elInput.checked = true;
        elInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.commit(elInput);
    }

    handleKeydown(ev) {
      if (this.#isDesktopGrid() || !this.open) return;

      if (ev.key === 'Escape') {
        ev.preventDefault();
        this.setAttribute('open', 'false');
        return;
      }

      // Let native Tab move focus; if it lands outside the host, close.
      // Checked on the next frame so document.activeElement has updated.
      if (ev.key === 'Tab') {
        requestAnimationFrame(() => {
          if (this.open && !this.contains(document.activeElement)) {
            this.suppressFocusReturn = true;
            this.setAttribute('open', 'false');
          }
        });
        return;
      }

      const elInput = ev.target.closest('input[type="radio"]');
      if (!elInput || !this.contains(elInput)) return;

      // Enter doesn't natively activate a radio; stop the form from
      // submitting and treat Enter as "commit + close".
      if (ev.key === 'Enter') {
        ev.preventDefault();
        this.commit(elInput);
        return;
      }

      // Space natively toggles the focused radio and fires `change`. Let that
      // happen first, then commit on the next frame so the display has been
      // updated with the latest selection.
      if (ev.key === ' ' || ev.key === 'Spacebar') {
        requestAnimationFrame(() => {
          const elChecked = [...this.elInputs].find(el => el.checked) || elInput;
          this.commit(elChecked);
        });
      }
    }

    handleDocumentPointerDown(ev) {
      if (!this.#closesOnOutsidePointer()) return;
      if (this.contains(ev.target)) return;
      this.suppressFocusReturn = true;
      this.setAttribute('open', 'false');
    }

    #isDesktopGrid() {
      return this.hasAttribute('desktop-grid') && this.#desktopGridMq.matches;
    }

    // Desktop only: on mobile, scrolling to reach options fires pointerdown
    // outside the host and would close the menu. Close via toggle or selection.
    #closesOnOutsidePointer() {
      return this.#desktopGridMq.matches;
    }

    #syncOutsideCloseListener() {
      if (this.isConnected && this.open && this.#closesOnOutsidePointer()) {
        document.addEventListener('pointerdown', this.handleDocumentPointerDown);
        return;
      }

      document.removeEventListener('pointerdown', this.handleDocumentPointerDown);
    }

    #syncDesktopGridMode() {
      const isDesktopGrid = this.#isDesktopGrid();

      if (isDesktopGrid) {
        this.suppressFocusReturn = true;

        if (this.open) {
          this.#updatingOpen = true;
          this.open = false;
          this.classList.remove('open');
          this.elToggle.setAttribute('aria-expanded', 'false');
          this.#updatingOpen = false;
        }

        this.elToggle.setAttribute('hidden', '');
        this.elOptions.removeAttribute('inert');
        this.#syncOutsideCloseListener();
        return;
      }

      this.elToggle.removeAttribute('hidden');

      if (this.elOptions.contains(document.activeElement)) {
        this.elToggle.focus({ preventScroll: true });
      }

      if (!this.open) {
        this.elOptions.setAttribute('inert', '');
      }

      this.#syncOutsideCloseListener();
    }

    #handleOpen() {
      this.elOptions.removeAttribute('inert');
      this.#syncOutsideCloseListener();

      const elToFocus = [...this.elInputs].find(el => el.checked) || this.elInputs[0];
      elToFocus && elToFocus.focus({ preventScroll: true });
    }

    #handleClose() {
      const hadFocusInside = this.contains(document.activeElement);
      this.elOptions.setAttribute('inert', '');
      this.#syncOutsideCloseListener();

      if (hadFocusInside && !this.suppressFocusReturn) {
        this.elToggle.focus({ preventScroll: true });
      }
      this.suppressFocusReturn = false;
    }
  }

  customElements.define('custom-select', CustomSelect);
}
